import React, { useState } from "react";
import {
  MapPin,
  Clock,
  DollarSign,
  User,
  ChevronDown,
  ChevronUp,
  Calendar,
  Zap,
  Star,
  AlertCircle,
  CheckCircle,
  Award,
  Eye,
  Heart,
  MessageCircle,
  Send,
  Share2,
} from "lucide-react";
import "./JobCard.css";

const JobCard = ({ jobData, onSendProposal }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  const handleSendProposal = () => {
    if (onSendProposal) {
      onSendProposal(jobData);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "urgent";
      case "high":
        return "high";
      case "medium":
        return "medium";
      case "low":
        return "low";
      default:
        return "medium";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const posted = new Date(dateString);
    const diffInHours = Math.floor((now - posted) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(dateString);
  };

  return (
    <div className={`job-card ${isExpanded ? "job-card--expanded" : ""}`}>
      {/* Background Image */}
      <div className="job-card__bg-image">
        <img
          src={
            jobData?.workDefaultImage ||
            "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=300&fit=crop"
          }
          alt="Job background"
        />
        <div className="job-card__overlay"></div>
      </div>

      {/* Header with Priority and Bonus */}
      <div className="job-card__header">
        <div className="job-card__badges">
          <div
            className={`job-card__priority ${getPriorityColor(
              jobData?.priority
            )}`}
          >
            {jobData?.priority?.toUpperCase() || "MEDIUM"}
          </div>
          <div className="job-card__bonus">
            <Zap className="bonus-icon" />₹{jobData?.bonus || "2000"} Bonus
          </div>
        </div>

        <div className="job-card__stats">
          <div className="job-card__views">
            <Eye className="stats-icon" />
            {jobData?.views || "420"}
          </div>
          {jobData?.featured && (
            <div className="job-card__crown">
              <Award className="crown-icon" />
            </div>
          )}
        </div>
      </div>

      {/* Job Category Badge */}
      <div className="job-card__category">
        {jobData?.category || "Electrician"}
      </div>

      {/* Client Info */}
      <div className="job-card__client">
        <div className="job-card__client-avatar">
          <img
            src={
              jobData?.clientAvatar ||
              `https://ui-avatars.com/api/?name=${jobData?.userFirstName}+${jobData?.userLastName}&background=3B82F6&color=fff`
            }
            alt="Client"
          />
          <div className="job-card__client-badge">
            <CheckCircle className="badge-icon" />
          </div>
        </div>
        <div className="job-card__client-info">
          <h4 className="job-card__client-name">
            {`${jobData?.userFirstName || "Sarah"} ${
              jobData?.userLastName || "Johnson"
            }`}
          </h4>
          <div className="job-card__client-rating">
            <Star className="rating-icon" />
            <span>4.9</span>
            <span className="time-ago">• {getTimeAgo(jobData?.postedAt)}</span>
          </div>
        </div>
        <div className="job-card__budget">
          <div className="budget-amount">₹{jobData?.maxBudget || "25,000"}</div>
          <div className="budget-label">Max Budget</div>
        </div>
      </div>

      {/* Job Title */}
      <h3 className="job-card__title">
        {jobData?.title || "Premium Electrical Wiring & Smart Home Setup"}
      </h3>

      {/* Location - Moved to top after title */}
      <div className="job-card__location-top">
        <MapPin className="location-icon" />
        <span>{jobData?.location || "Kozhikode, Kerala"}</span>
      </div>

      {/* Action Buttons - Moved after location */}
      <div className="job-card__actions">
        <button
          className="action-btn action-btn--secondary"
          onClick={handleLike}
        >
          <Heart className={`btn-icon ${isLiked ? "liked" : ""}`} />
        </button>
        <button className="action-btn action-btn--secondary">
          <MessageCircle className="btn-icon" />
        </button>
        <button className="action-btn action-btn--secondary">
          <Share2 className="btn-icon" />
        </button>
        <button
          className="action-btn action-btn--primary"
          onClick={handleSendProposal}
        >
          <Send className="btn-icon" />
          Send Proposal
        </button>
      </div>

      {/* Expandable Content - Scrollable */}
      <div className="job-card__expandable">
        <div className="job-card__expandable-content">
          {/* Job Description */}
          <div className="job-card__description">
            <h4>Job Description</h4>
            <p>
              {jobData?.jobDescription ||
                "Transform my kitchen with modern electrical work + smart home automation. Looking for certified expert who can handle complex wiring, install smart switches, and integrate home automation systems."}
            </p>
          </div>

          {/* Tags */}
          <div className="job-card__tags-section">
            <h4>Required Skills</h4>
            <div className="job-card__tags">
              {(
                jobData?.tags || [
                  "Licensed Required",
                  "Smart Home",
                  "High Pay",
                  "Electrical Work",
                  "Home Automation",
                ]
              ).map((tag, index) => (
                <span key={index} className="job-card__tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="job-card__timeline-section">
            <h4>Timeline</h4>
            <div className="job-card__timeline">
              <Clock className="meta-icon" />
              <span>Next 3 Days</span>
            </div>
          </div>

          {/* Client Details */}
          <div className="job-card__client-details">
            <h4>Client Information</h4>
            <div className="detail-item">
              <strong>Name:</strong>{" "}
              {`${jobData?.userFirstName || "Sarah"} ${
                jobData?.userLastName || "Johnson"
              }`}
            </div>
            <div className="detail-item">
              <strong>Posted:</strong> {formatDate(jobData?.postedAt)}
            </div>
            <div className="detail-item">
              <strong>Status:</strong> {jobData?.status || "Active"}
            </div>
          </div>

          {/* Footer Stats */}
          <div className="job-card__footer">
            <div className="job-card__applications">
              <User className="footer-icon" />
              <span>{jobData?.applicationsCount || "24"} applied</span>
            </div>
            <div className="job-card__likes">
              <Heart className="footer-icon" />
              <span>{jobData?.likesCount || "156"}</span>
            </div>
            <div className="job-card__range">
              <span>
                Range: ₹{jobData?.minBudget || "15,000"} - ₹
                {jobData?.maxBudget || "25,000"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expand/Collapse Button */}
      <button className="job-card__expand-btn" onClick={toggleExpand}>
        {isExpanded ? <ChevronUp /> : <ChevronDown />}
      </button>
    </div>
  );
};

export default JobCard;
